import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "La adicción al vibecoding";
export const excerpt =
  "El vibe coding funciona con el mismo programa de recompensas que una máquina tragaperras: pagos impredecibles, casi aciertos que se sienten como avances y el tirón de un prompt más. La investigación empieza a ponerse al día.";
export const heroAlt =
  "Paisaje estilo Moebius: un sendero de luces serpenteante y brillante que cruza una vasta llanura de hierba hacia un horizonte al atardecer, figuras diminutas mirando desde una duna florida bajo nubes pastel";
export const readingTime = "6 min de lectura";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "M. Fawzy, A. Tahir, K. Blincoe, «Vibe Coding in Practice: Motivations, Challenges, and a Future Outlook: A Grey Literature Review» (2025). Se usa para el hallazgo de que el 64 % de los profesionales declararon «éxito inmediato y flow», descrito como mágico, un subidón de dopamina y un impulso adictivo, y para la falsa confianza resultante en la calidad del código.",
    href: "https://arxiv.org/abs/2510.00328",
    label: "arxiv.org/abs/2510.00328",
  },
  {
    text: "R. Thomas, «Breaking the Spell of Vibe Coding», fast.ai (enero de 2026). Se usa para el planteamiento del dark flow / junk flow, la mecánica de la pérdida disfrazada de victoria y el contraste con el flow constructor de habilidades de Csikszentmihalyi.",
    href: "https://www.fast.ai/posts/2026-01-28-dark-flow/",
    label: "fast.ai/posts/2026-01-28-dark-flow",
  },
  {
    text: "L. Clark, A. J. Lawrence, F. Astley-Jones, N. Gray, «Gambling Near-Misses Enhance Motivation to Gamble and Recruit Win-Related Brain Circuitry», Neuron (2009). Se usa para el hallazgo de que los casi aciertos activan la circuitería cerebral relacionada con la victoria y aumentan las ganas de seguir jugando pese a no haber recompensa monetaria.",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2658737/",
    label: "ncbi.nlm.nih.gov/…/Clark-2009-near-misses",
  },
  {
    text: "H. W. Chase, L. Clark, «Gambling Severity Predicts Midbrain Response to Near-Miss Outcomes», Journal of Neuroscience (2010). Se usa para el hallazgo de que la intensidad de la respuesta dopaminérgica del mesencéfalo ante los casi aciertos se correlacionaba con la gravedad de la ludopatía.",
    href: "https://www.jneurosci.org/content/30/18/6180",
    label: "jneurosci.org/content/30/18/6180",
  },
  {
    text: "METR, «Measuring the Impact of Early-2025 AI on Experienced Open-Source Developer Productivity» (2025). Se usa para el ensayo aleatorizado en el que los desarrolladores esperaban una aceleración de ~24 %, percibieron una aceleración de ~20 % y se midió que eran un 19 % más lentos con las herramientas de IA.",
    href: "https://arxiv.org/abs/2507.09089",
    label: "arxiv.org/abs/2507.09089",
  },
  {
    text: "A. Filev, «One More Prompt: How Vibe Coding’s Casino Mechanics Cost Me a Billion Dollars», Unite.AI (octubre de 2025). Se usa para el planteamiento de la recompensa variable y el casi acierto, el bucle del «un prompt más» y la idea de que el tirón es más fuerte para quien acaba de empezar a construir software.",
    href: "https://www.unite.ai/one-more-prompt-how-vibe-codings-casino-mechanics-cost-me-a-billion-dollars/",
    label: "unite.ai/…/one-more-prompt-casino-mechanics",
  },
  {
    text: "«Professional Software Developers Don’t Vibe, They Control: AI Agent Use for Coding in 2025» (2025). Se usa para el hallazgo de que los desarrolladores con experiencia tienden a pilotar y rechazar la salida de la IA en lugar de rendirse al bucle.",
    href: "https://arxiv.org/abs/2512.14012",
    label: "arxiv.org/abs/2512.14012",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        Quiero describir una noche concreta, porque creo que mucha gente ha tenido la misma. Abres
        el editor para arreglar una cosita. Escribes un prompt y el modelo se acerca. No del todo, pero casi.
        Así que escribes otro, y otro. En algún momento levantas la vista y han pasado tres horas, la funcionalidad sigue
        sin funcionar y tu sensación más fuerte son las ganas de mandar un prompt más.
      </Lead>

      <P>
        Esa última parte es la señal. No el tiempo perdido, también se pierde tiempo con el buen trabajo. La señal es el tirón,
        las ganas de seguir que, en silencio, se han desligado de si la cosa va bien o no.
      </P>
      <P>
        Ya existe un montón pequeño pero creciente de textos, algunos académicos, que sostienen que el vibe coding no es
        simplemente productivo o improductivo. Es estructuralmente adictivo, casi en el sentido técnico. Funciona
        con el mismo programa de recompensas que hace difícil apartarse de una máquina tragaperras.
      </P>

      <Section title="Un prompt más">
        <P>
          Andrew Filev, que dirige una empresa de programación con IA, escribió sobre cómo se sorprendió a sí mismo en ello. Estaba de vacaciones,
          la familia esperándolo en la playa, diciéndose «solo un intento más» durante horas mientras el
          prompt casi funcionaba.<Fn n={6} /> No es un usuario ocasional. Se gana la vida construyendo estas herramientas.
          Aun así acaba describiendo la experiencia con el lenguaje de un casino: recompensas
          impredecibles, casi aciertos, el bucle del «un prompt más» que no lograba cerrar del todo.
        </P>
        <P>
          Lo que hace útil su relato es que es honesto sobre a quién golpea con más fuerza. Para un ingeniero
          con experiencia, la IA es una optimización, una versión más rápida de algo que ya sabía hacer. Para alguien
          que no sabía construir software en absoluto y de repente puede, el salto es enorme, y también lo es la
          dopamina.<Fn n={6} /> Cuanto mayor es la distancia entre «no podía» y «acabo de
          hacerlo», más fuerte es el tirón.
        </P>
      </Section>

      <Section title="Funciona con el mismo programa que una máquina tragaperras">
        <P>
          La psicología conductual tiene un nombre para el patrón de recompensa más tozudo que conocemos: el programa de
          razón variable. Recibes una recompensa tras un número impredecible de intentos. No siempre, no con una cuenta
          fija, solo lo bastante a menudo como para que nunca puedas predecir el próximo pago. Palomas, ratas y personas
          seguirán accionando una palanca mucho más tiempo bajo ese programa que bajo uno predecible. Es el motor
          que hay dentro de una máquina tragaperras.
        </P>
        <P>
          El vibe coding encaja en el patrón casi demasiado bien. A veces el primer prompt lo clava. A veces lo hace el
          octavo, y a veces quemas una hora para nada. El pago es real, código que funciona, pero su
          momento es aleatorio, y el momento aleatorio es exactamente lo que mantiene la palanca en movimiento.
        </P>
        <P>
          La programación de toda la vida pagaba despacio. Podías pasar horas entre las pequeñas victorias de que algo compilara
          o de que una prueba se pusiera en verde. La programación con IA comprime eso a segundos. La revisión de literatura gris a la que
          no dejo de volver encontró que la experiencia más común, el 64 por ciento de los profesionales de
          su muestra, era «éxito inmediato y flow», descrito a menudo con esas mismas palabras:
          mágico, un subidón de dopamina, un impulso adictivo.<Fn n={1} /> Pagos rápidos con un programa aleatorio, que es
          toda la receta con la que funciona un casino.
        </P>
      </Section>

      <Section title="El anzuelo">
        <P>
          La comparación con la tragaperras se afina en el casi acierto. Un casi acierto es la tirada en la que dos cerezas
          caen en la línea y la tercera se para justo por encima. Objetivamente es una pérdida, idéntica a cualquier otra
          pérdida. No se siente como tal.
        </P>
        <P>
          Luke Clark y sus colegas metieron a jugadores en un escáner de fMRI y mostraron que los casi aciertos activan buena parte de
          la misma circuitería cerebral relacionada con la victoria que los aciertos reales, y que las personas declaraban un impulso más fuerte de seguir
          jugando justo después de un casi acierto, aunque no hubieran ganado nada.<Fn n={3} /> Un estudio posterior encontró
          que el tamaño de esa respuesta del mesencéfalo ante los casi aciertos se correlacionaba con lo grave que era la
          ludopatía de la persona.<Fn n={4} /> El casi acierto no es un estado de fracaso para el cerebro. Es un segundo tipo de
          recompensa.
        </P>
        <P>
          Ahora piensa en lo que produce un agente de programación cuando se equivoca. No basura. Normalmente algo que
          casi funciona. El código compila y falla una prueba. La página se renderiza pero el botón no hace nada. Es
          el casi acierto, generado a demanda, decenas de veces por hora. Cada uno dice que estás cerquísima, inténtalo
          otra vez, y a diferencia de una tragaperras a menudo dice la verdad, lo que lo hace mucho más difícil de ignorar.
        </P>
      </Section>

      <Section title="Flow, o una copia barata de él">
        <P>
          Quienes defienden el vibe coding recurren a la palabra flow, y es un instinto razonable. El estado se siente como
          flow: absorto, el tiempo se esfuma, la fricción baja. Rachel Thomas, de fast.ai, argumenta que está más cerca de
          lo que los investigadores llaman dark flow o junk flow, el que produce una máquina tragaperras o un feed
          infinito.<Fn n={2} /> El flow de verdad, en el sentido original de Csikszentmihalyi, surge de una tarea ajustada a
          tu habilidad y construye la habilidad a medida que avanzas. El junk flow es la absorción sin el crecimiento. Se siente
          igual desde dentro, y sales por el otro lado sin haber mejorado, a veces peor.
        </P>
        <P>
          La revisión de literatura gris encontró el mismo lado oscuro en sus datos. La experiencia de éxito inmediato producía
          lo que los autores llaman falsa confianza en la calidad del código.<Fn n={1} /> La sensación de haber
          hecho un buen trabajo y el hecho de haberlo hecho se habían separado. Esa es la parte que debería preocuparte,
          más que las horas perdidas.
        </P>
      </Section>

      <Section title="Parece un avance. Los números lo desmienten.">
        <P>
          Los investigadores de las tragaperras tienen un término para el truco más cruel del casino: una pérdida disfrazada de victoria. La
          máquina devuelve menos de lo apostado pero aun así lanza toda la canción y las luces, así que una pérdida neta se registra
          como una victoria.<Fn n={2} /> El vibe coding hace algo parecido con la productividad. Se siente rápido. Es ruidoso
          sobre su propio avance. El resultado medido es menos halagüeño.
        </P>
        <P>
          METR realizó un ensayo aleatorizado a principios de 2025 con desarrolladores de código abierto con experiencia trabajando en tareas
          reales en sus propios repositorios. De antemano, los desarrolladores esperaban que la IA los acelerara alrededor de un 24
          por ciento. Después, creían que los había acelerado alrededor de un 20 por ciento. Medidos, eran un 19
          por ciento más lentos con las herramientas de IA que sin ellas.<Fn n={5} /> Se sentían más rápidos y eran más lentos, una brecha de
          casi 40 puntos entre lo que sentían y lo que ocurría. En términos de tragaperras, eso es una pérdida disfrazada de
          victoria, esta vez medida en un ensayo controlado con ingenieros profesionales.
        </P>
      </Section>

      <Section title="¿Es realmente una adicción?">
        <P>
          Quiero ser cuidadoso aquí, porque es fácil exagerar. No existe un diagnóstico llamado
          trastorno del vibe coding. Nadie ha realizado los largos estudios clínicos que harían falta para llamar a esto una
          adicción en el sentido en que la ludopatía es una adicción. Lo que tenemos es un mecanismo que coincide,
          recompensas de razón variable y casi aciertos potentes, más un montón de gente que, de forma independiente, recurre al
          mismo vocabulario del juego para describir su propia conducta. Eso es sugerente, no concluyente.
        </P>
        <P>
          Tampoco golpea a todos por igual. Un estudio reciente con el título contundente «Professional
          Software Developers Don’t Vibe, They Control» encontró que los ingenieros con experiencia tienden a no
          rendirse al bucle en absoluto. Pilotan al agente, rechazan la mayor parte de lo que ofrece y mantienen un modelo del
          sistema en su propia cabeza.<Fn n={7} /> El tirón es más fuerte justo donde las barreras de seguridad son
          más finas: personas que acaban de empezar a construir software, yendo rápido, sin pruebas y sin una idea clara de cuándo
          han terminado.
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="Referencias">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
